// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState } from 'react';
import { Eye, Loader2, MessageSquare, Send, Smartphone } from 'lucide-react';
import { createCampaign, executeCampaign } from '../../lib/backOfficeApi';
import { toast } from '../../lib/buses';
import { Modal } from '../../ui/Modal';

// segment → the real backend audienceType the campaign resolver supports
// (buyers = successful-order recipients for the event; subscribers = the
// event's subscribers/waitlist). No fabricated paid/unpaid/vip filters.
const SEGMENTS = [
  { id: 'buyers',      label: 'Ticket buyers',        audienceType: 'buyers' },
  { id: 'subscribers', label: 'Subscribers & waitlist', audienceType: 'subscribers' },
];

export const BroadcastComposerModal = ({ open, onClose, onSent, audienceLabel, audienceSize, eventId }) => {
  const [channels, setChannels] = useState({ email: true, sms: false, whatsapp: false });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [segment, setSegment] = useState('buyers');
  const [schedule, setSchedule] = useState('now');
  const [busy, setBusy] = useState(false);
  // Adjust state during render (not in an effect) when `open` changes — see
  // react.dev "Adjusting some state when a prop changes".
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) { setChannels({ email: true, sms: false, whatsapp: false }); setSubject(''); setBody(''); setSegment('buyers'); setSchedule('now'); setBusy(false); }
  }
  if (!open) return null;
  const channelCount = Object.values(channels).filter(Boolean).length;
  const estReach = segment === 'buyers' && audienceSize ? audienceSize : null;

  const send = async () => {
    if (!eventId) { toast('No event audience', 'error', 'Open the broadcast from a specific event.'); return; }
    const chans = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (!chans.length || !body.trim()) return;
    const audienceType = (SEGMENTS.find((s) => s.id === segment) || SEGMENTS[0]).audienceType;
    const sendNow = schedule === 'now';
    const baseName = (subject.trim() || `${audienceLabel || 'Broadcast'}`).slice(0, 80);
    setBusy(true);
    try {
      // One real campaign per channel (the backend takes a single channel).
      const results = await Promise.allSettled(chans.map(async (ch) => {
        const c = await createCampaign({
          name: chans.length > 1 ? `${baseName} · ${ch}` : baseName,
          channel: ch, eventId, scope: 'event',
          ...(ch === 'email' && subject.trim() ? { subject: subject.trim() } : {}),
          content: body.trim(),
          audience: { audienceSources: [{ audienceType, sourceId: eventId }] },
        });
        if (sendNow && c?.id) await executeCampaign(c.id);
        return ch;
      }));
      const ok = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      const failed = chans.length - ok.length;
      if (ok.length) {
        toast(sendNow ? 'Broadcast sent' : 'Broadcast saved as draft', failed ? 'warning' : 'success',
          `${ok.join(' + ')}${estReach ? ` · ~${estReach.toLocaleString()} ${segment}` : ''}${sendNow ? '' : ' · execute from Comms to send'}${failed ? ` · ${failed} failed` : ''}`);
        onSent && onSent();
        onClose();
      } else {
        toast('Broadcast failed', 'error', 'The backend rejected every channel');
      }
    } catch (e) { toast('Broadcast failed', 'error', e?.message || 'Backend rejected the request'); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} width="max-w-[640px]" title="Compose broadcast" sub={audienceLabel || (eventId ? `Audience for ${eventId}` : 'Customer broadcast')}>
      <div className="space-y-4">
        <div>
          <label className="block text-[11px] font-medium text-[#1F2937] mb-1.5">Channels</label>
          <div className="flex gap-2">
            {[
              { id: 'email',    label: 'Email',    icon: Send },
              { id: 'sms',      label: 'SMS',      icon: Smartphone },
              { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
            ].map(c => {
              const Icon = c.icon;
              const active = channels[c.id];
              return (
                <button key={c.id} onClick={() => setChannels({ ...channels, [c.id]: !active })} className={`h-9 px-3 border rounded-md text-[12px] font-medium inline-flex items-center gap-1.5 ${active ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#4B5563] border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                  <Icon className="w-3.5 h-3.5" strokeWidth={2} />{c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-medium text-[#1F2937] mb-1.5">Audience segment</label>
            <select value={segment} onChange={e => setSegment(e.target.value)} className="w-full h-9 bg-white border border-[#E6E4E1] rounded-md px-2.5 text-[12.5px] focus:outline-none focus:border-[#D8D5CF]">
              {SEGMENTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#1F2937] mb-1.5">Schedule</label>
            <select value={schedule} onChange={e => setSchedule(e.target.value)} className="w-full h-9 bg-white border border-[#E6E4E1] rounded-md px-2.5 text-[12.5px] focus:outline-none focus:border-[#D8D5CF]">
              <option value="now">Send immediately</option>
              <option value="draft">Save as draft</option>
            </select>
          </div>
        </div>

        {channels.email && (
          <div>
            <label className="block text-[11px] font-medium text-[#1F2937] mb-1.5">Email subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Update on your tickets" className="w-full h-9 bg-white border border-[#E6E4E1] rounded-md px-2.5 text-[12.5px] focus:outline-none focus:border-[#D8D5CF]" />
          </div>
        )}

        <div>
          <label className="block text-[11px] font-medium text-[#1F2937] mb-1.5">Message body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} placeholder="Hi {firstName}, …" className="w-full bg-white border border-[#E6E4E1] rounded-md px-2.5 py-2 text-[12.5px] focus:outline-none focus:border-[#D8D5CF] resize-none" />
          <div className="text-[10.5px] text-[#9CA3AF] mt-1">Variables available: {'{firstName}'}, {'{eventName}'}, {'{ticketRef}'}, {'{venue}'}</div>
        </div>

        <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-3 text-[11.5px] text-[#4B5563]">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-[#1F2937]">Estimated reach</span>
            <span className="tabular-nums font-mono text-[12px] text-[#1F2937]">{estReach ? estReach.toLocaleString() : '—'} recipients</span>
          </div>
          <div className="text-[11px] text-[#6B7280]">{channelCount} channel{channelCount !== 1 ? 's' : ''} · {segment === 'all' ? 'all buyers' : segment} · {schedule === 'now' ? 'immediate' : 'scheduled'}</div>
        </div>
      </div>
      <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] -mx-5 -mb-4 mt-4 flex items-center justify-between">
        <button onClick={() => toast('Test send isn’t wired', 'info', 'Send a real broadcast to the buyer audience, or preview a template in Comms')} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED] inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />Send test</button>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
          <button onClick={send} disabled={busy || !body.trim() || channelCount === 0} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white inline-flex items-center gap-1.5">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}{schedule === 'now' ? 'Send now' : 'Save as draft'}</button>
        </div>
      </div>
    </Modal>
  );
};

